import { useQuery } from '@tanstack/react-query'

import { fetchWithHeaders } from 'utils'

import { GrapeAPIError, type GrapeAPIErrorType } from './GrapeAPIError'
import type { GetSamplePayPeriodsParams, PayPeriod } from '../../types/payroll'

type ResponseType = {
  data: PayPeriod[]
}

export function useGetSamplePayPeriods (params: GetSamplePayPeriodsParams) {
  return useQuery<ResponseType, GrapeAPIErrorType>({
    queryKey: ['paySchedules', 'preview', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        frequency: params.frequency,
        bank_closure_strategy: params.bank_closure_strategy,
        ...(params.reference_pay_date && { reference_pay_date: params.reference_pay_date }),
        ...(params.first_day && { first_day: params.first_day.toString() }),
        ...(params.second_day && { second_day: params.second_day.toString() }),
        ...(params.start_date && { start_date: params.start_date }),
      }).toString()

      const response = await fetchWithHeaders(`/api/web/payroll/pay_schedules/preview?${queryParams}`)
      if (!response.ok) {
        const errorJson = await response.json()
        throw GrapeAPIError({
          status: response.status,
          message: errorJson.message,
          errors: errorJson.errors
        })
      }
      return response.json()
    },
    enabled: !!(
      params.frequency &&
      params.bank_closure_strategy &&
      // The following is a temp fix so we don't make preview requests when first_day and/or second_day is missing.
      // Ideally we'd just check `isValid` to confirm that first_day and second_day are conditionally present depending
      // on the pay frequency type, but it doesn't seem to be working when they're set and then unset
      (params.frequency === 'weekly' ||
        params.frequency === 'bi_weekly' ||
        (params.frequency === 'monthly' && params.first_day) ||
        (params.frequency === 'semi_monthly' && params.first_day && params.second_day)
      ) && params.isValid
    )
  })
}
